import { useQuery } from "@apollo/client";
import { DocumentNode, GraphQLError } from "graphql";
import React, { useCallback, useEffect } from "react";
import ContentLoader, { Circle, Rect } from "react-content-loader/native";
import { useFocusEffect } from "@react-navigation/native";

import { V3_CLIENT_NAME } from "../../components/GraphqlProvider";
import { ProfileQuery, EnrollmentQuery } from "./queries";
import { useAppState } from "../../hooks";

type SkeletonLoadingProps = {
  query: DocumentNode;
  LoadingComp?: React.ElementType;
  children(data: any, refetch: () => void): JSX.Element | null;
};

export const SkeletonLoading = ({ query, LoadingComp, children }: SkeletonLoadingProps) => {
  const { loading, data, refetch } = useQuery<EnrollmentQuery>(query);
  if (loading && !data) return LoadingComp ? <LoadingComp /> : null;
  return children(data, refetch);
};

export const QuerySkeletonLoading = ({
  query,
  clientName,
  LoadingComp,
  children,
}: SkeletonLoadingProps & { clientName?: string }) => {
  const context = {
    clientName,
    callerWillHandleError: (_: GraphQLError[], networkError: Error) => !networkError,
  };
  const { loading, data, refetch } = useQuery<ProfileQuery>(query, {
    context,
    errorPolicy: "all",
  });
  const { isAppActive } = useAppState();

  useEffect(() => {
    if (isAppActive) refetch();
  }, [isAppActive]);

  useFocusEffect(
    useCallback(() => {
      refetch();
    }, [])
  );

  if (loading && !data) return LoadingComp ? <LoadingComp /> : null;
  return children(data, refetch);
};

export const V3SkeletonLoading = ({ query, LoadingComp, children }: SkeletonLoadingProps) => {
  return (
    <QuerySkeletonLoading query={query} clientName={V3_CLIENT_NAME} LoadingComp={LoadingComp}>
      {children}
    </QuerySkeletonLoading>
  );
};

const DEFAULT_PROPS = {
  speed: 2,
  backgroundColor: "#efefef",
  foregroundColor: "#e4e1e1",
  width: "100%",
};

export const ProfileLoader = () => (
  <ContentLoader height={290} {...DEFAULT_PROPS}>
    <Circle cx="30" cy="30" r="30" />
    <Rect x="0" y="80" rx="4" ry="4" width="50%" height="24" />
    <Rect x="0" y="120" rx="4" ry="4" width="75%" height="12" />
    <Rect x="0" y="145" rx="4" ry="4" width="75%" height="12" />
    <Rect x="0" y="175" rx="4" ry="4" width="100%" height="1" />
    <Rect x="0" y="192" rx="4" ry="4" width="50%" height="12" />
    <Rect x="0" y="225" rx="4" ry="4" width="100%" height="1" />
    <Rect x="0" y="250" rx="4" ry="4" width="75%" height="12" />
  </ContentLoader>
);

export const PdrLoader = () => (
  <ContentLoader height={400} {...DEFAULT_PROPS}>
    <Rect x="0" y="10" rx="4" ry="4" width="50%" height="24" />
    <Rect x="0" y="60" rx="4" ry="4" width="25%" height="24" />
    <Rect x="0" y="95" rx="4" ry="4" width="100%" height="12" />
    <Rect x="0" y="140" rx="4" ry="4" width="25%" height="24" />
    <Rect x="0" y="175" rx="4" ry="4" width="100%" height="12" />

    <Rect x="0" y="220" rx="4" ry="4" width="25%" height="24" />
    <Rect x="0" y="255" rx="4" ry="4" width="100%" height="12" />
    <Rect x="0" y="280" rx="8" ry="8" width="100%" height="48" />

    <Rect x="0" y="350" rx="4" ry="4" width="100%" height="1" />
    <Rect x="0" y="370" rx="4" ry="4" width="50%" height="12" />
  </ContentLoader>
);

export const DuesLoader = () => (
  <ContentLoader height={240} {...DEFAULT_PROPS}>
    <Rect x="0" y="10" rx="4" ry="4" width="50%" height="24" />
    <Rect x="0" y="58" rx="4" ry="4" width="100%" height="12" />
    <Rect x="0" y="94" rx="4" ry="4" width="100%" height="12" />
    <Rect x="0" y="122" rx="4" ry="4" width="100%" height="1" />
    <Rect x="0" y="145" rx="4" ry="4" width="50%" height="12" />
    <Rect x="0" y="178" rx="4" ry="4" width="100%" height="1" />
    <Rect x="0" y="204" rx="4" ry="4" width="50%" height="12" />
  </ContentLoader>
);

export const AnnouncementLoader = () => (
  <ContentLoader height={220} {...DEFAULT_PROPS}>
    <Rect x="0" y="10" rx="4" ry="4" width="100%" height="24" />
    <Rect x="0" y="70" rx="4" ry="4" width="100%" height="12" />
    <Rect x="0" y="90" rx="4" ry="4" width="100%" height="12" />
    <Rect x="0" y="110" rx="4" ry="4" width="100%" height="12" />
    <Rect x="0" y="180" rx="4" ry="4" width="50%" height="12" />
  </ContentLoader>
);

export const InvoiceLoader = () => (
  <ContentLoader height={300} {...DEFAULT_PROPS}>
    <Rect x="0" y="70" rx="4" ry="4" width="100%" height="12" />
    <Rect x="0" y="90" rx="4" ry="4" width="100%" height="12" />
    <Rect x="0" y="110" rx="4" ry="4" width="100%" height="12" />

    <Rect x="0" y="150" rx="4" ry="4" width="100%" height="12" />
    <Rect x="0" y="170" rx="4" ry="4" width="100%" height="12" />
    <Rect x="0" y="190" rx="4" ry="4" width="100%" height="12" />
  </ContentLoader>
);
